// webapp/src/lib/diag/layoutTap.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
let _installed = false;
let hotUntil = 0;
let hotLabel = 'hot';
let defaultWindowMs = 1200;

type Cfg = {
  windowMs?: number;   // hot window length, default 1200ms
  minCount?: number;   // min calls per stack to report, default 5
  maxStacks?: number;  // max stacks to print, default 6
};

const calls = new Map<string, number>();
let flushTimer: number | null = null;

function now() { return performance.now(); }
function inHotWindow() { return now() <= hotUntil; }

function mark(label = 'hot', ms = defaultWindowMs) {
  hotLabel = label;
  hotUntil = now() + ms;
  // eslint-disable-next-line no-console
  console.warn('[layouttap] mark', { label, ms });
}

function rec(stack: string) {
  const n = (calls.get(stack) || 0) + 1;
  calls.set(stack, n);
  if (!flushTimer) {
    flushTimer = window.setTimeout(flush, 700);
  }
}

function flush(cfg: Cfg = {}) {
  const arr = [...calls.entries()];
  calls.clear();
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (arr.length === 0) return;

  const minCount = cfg.minCount ?? 5;
  const maxStacks = cfg.maxStacks ?? 6;

  arr.sort((a, b) => b[1] - a[1]);
  const top = arr.filter(([, c]) => c >= minCount).slice(0, maxStacks);
  if (top.length) {
    // eslint-disable-next-line no-console
    console.warn('[layouttap] hot-window layout reads', {
      label: hotLabel,
      untilInMs: Math.round(Math.max(0, hotUntil - now())),
      stacks: top.map(([s, c]) => ({ count: c, stack: s })),
      totalUnique: arr.length,
      totalCalls: arr.reduce((t, [, c]) => t + c, 0),
    });
  }
}

function shortStack(): string {
  return (new Error().stack || '')
    .split('\n')
    .slice(3, 12) // trim wrapper frames
    .join('\n');
}

function wrapGetter<T extends object>(proto: T, key: string) {
  const desc = Object.getOwnPropertyDescriptor(proto, key as any);
  if (!desc || !desc.get || !desc.configurable) return;
  const orig = desc.get;
  Object.defineProperty(proto, key, {
    configurable: true,
    get: function wrapped(this: any) {
      const v = orig.call(this);
      if (inHotWindow()) rec(`${key}\n${shortStack()}`);
      return v;
    }
  });
}

function wrapMethod<T extends object>(proto: T, key: string) {
  const orig = (proto as any)[key];
  if (typeof orig !== 'function') return;
  Object.defineProperty(proto, key, {
    configurable: true,
    writable: true,
    value: function wrapped(this: any, ...args: any[]) {
      const out = orig.apply(this, args);
      if (inHotWindow()) rec(`${key}()\n${shortStack()}`);
      return out;
    }
  });
}

export function installLayoutTap(cfg: Cfg = {}) {
  if (_installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  _installed = true;

  defaultWindowMs = cfg.windowMs ?? 1200;

  // Reads that can sync-flush layout:
  wrapMethod(Element.prototype, 'getBoundingClientRect');

  wrapGetter(HTMLElement.prototype, 'offsetWidth');
  wrapGetter(HTMLElement.prototype, 'offsetHeight');
  wrapGetter(HTMLElement.prototype, 'offsetTop');
  wrapGetter(HTMLElement.prototype, 'offsetLeft');

  wrapGetter(Element.prototype, 'scrollWidth');
  wrapGetter(Element.prototype, 'scrollHeight');

  // getComputedStyle often pairs with reads; include for signal
  const _gcs = window.getComputedStyle.bind(window);
  window.getComputedStyle = function wrapped(el: Element, pseudoElt?: string | null) {
    const r = _gcs(el, pseudoElt as any);
    if (inHotWindow()) rec(`getComputedStyle()\n${shortStack()}`);
    return r;
  };

  // Optional: log slow ResizeObserver/MutationObserver callbacks
  const _RO = (window as any).ResizeObserver as any;
  if (_RO) {
    (window as any).ResizeObserver = class RO extends _RO {
      constructor(cb: any) {
        super(function wrapped(...args: any[]) {
          const t0 = performance.now();
          cb(...args);
          const dt = performance.now() - t0;
          if (dt > 32 && inHotWindow()) {
            // eslint-disable-next-line no-console
            console.warn('[layouttap] slow ResizeObserver', Math.round(dt), 'ms', '\n' + shortStack());
          }
        });
      }
    };
  }
  const _MO = (window as any).MutationObserver as any;
  if (_MO) {
    (window as any).MutationObserver = class MO extends _MO {
      constructor(cb: any) {
        super(function wrapped(...args: any[]) {
          const t0 = performance.now();
          cb(...args);
          const dt = performance.now() - t0;
          if (dt > 32 && inHotWindow()) {
            // eslint-disable-next-line no-console
            console.warn('[layouttap] slow MutationObserver', Math.round(dt), 'ms', '\n' + shortStack());
          }
        });
      }
    };
  }

  // Expose a global mark for click paths
  (window as any).__DIAG_MARK__ = (label?: string, ms?: number) => mark(label, ms ?? defaultWindowMs);

  // Auto-flush occasionally so we don’t keep state forever
  window.setInterval(() => flush(cfg), 1500);
}

export function markHotWindow(label?: string, ms?: number) {
  mark(label, ms ?? defaultWindowMs);
}
